import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../App';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  LocalFireDepartment,
  TrendingUp,
  People,
  AttachMoney,
  Code
} from '@mui/icons-material';
import TweetCarousel from '../components/TweetCarousel';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const IdeaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadIdea();
    checkUser();
  }, [id]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const loadIdea = async () => {
    try {
      // Load idea
      const { data: ideaData, error: ideaError } = await supabase
        .from('product_ideas')
        .select('*')
        .eq('id', id)
        .single();

      if (ideaError) throw ideaError;
      setIdea(ideaData);

      // Load related tweets through evidence posts
      const { data: evidenceData, error: evidenceError } = await supabase
        .from('idea_evidence_posts')
        .select('post_id, relevance_score')
        .eq('idea_id', id)
        .order('relevance_score', { ascending: false })
        .limit(10);

      if (!evidenceError && evidenceData && evidenceData.length > 0) {
        const tweets = evidenceData.map((e, idx) => ({
          post_id: e.post_id,
          id: idx
        }));
        setTweets(tweets);
      }
    } catch (error) {
      console.error('Error loading idea:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKillIt = async () => {
    if (!user) {
      setLoginModalOpen(true);
      return;
    }

    // Navigate to project page with the idea as initial message
    const initialMessage = `I want to build: ${idea.name}\n\n${idea.tagline || ''}\n\nProblem: ${idea.problem_statement}\n\nTarget audience: ${idea.target_audience}`;

    navigate('/chat', {
      state: {
        initialMessage,
        ideaId: idea.id,
        ideaName: idea.name
      }
    });
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/idea/${id}?action=kill`
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error('Google login error:', err);
      alert('Login failed: ' + err.message);
      setLoginLoading(false);
    }
  };

  const handleXLogin = async () => {
    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'twitter',
        options: {
          redirectTo: `${window.location.origin}/idea/${id}?action=kill`
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error('X login error:', err);
      alert('Login failed: ' + err.message);
      setLoginLoading(false);
    }
  };

  // Check if redirected back after login with action=kill
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'kill' && user && idea) {
      // Clear the URL param and trigger kill action
      window.history.replaceState({}, '', `/idea/${id}`);
      handleKillIt();
    }
  }, [user, idea]);

  const getScoreColor = (score) => {
    if (score >= 8) return '#10b981';
    if (score >= 6) return '#f59e0b';
    return '#6b7280';
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (!idea) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography sx={{ color: '#9ca3af' }}>Idea not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
      pb: 4,
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        background: 'rgba(15, 15, 15, 0.9)',
        backdropFilter: 'blur(10px)',
        zIndex: 10
      }}>
        <Box sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          maxWidth: '1400px',
          mx: 'auto'
        }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
          <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
            Pain Point Cure
          </Typography>
        </Box>
      </Box>

      {/* Content - Responsive Layout */}
      <Box sx={{
        px: 2,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 380px' },
        gap: { xs: 0, md: 4 },
        maxWidth: '1400px',
        mx: 'auto',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* Main Content */}
        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        {/* Category & Pain Score */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            label={idea.category || 'General'}
            sx={{
              background: 'rgba(99, 102, 241, 0.2)',
              color: '#a5b4fc',
              fontSize: '12px'
            }}
          />
          <Chip
            icon={<LocalFireDepartment sx={{ fontSize: 16, color: getScoreColor(idea.pain_severity || 5) }} />}
            label={`Pain: ${idea.pain_severity?.toFixed(1) || '5.0'}`}
            sx={{
              background: 'rgba(255,255,255,0.05)',
              color: getScoreColor(idea.pain_severity || 5),
              fontSize: '12px'
            }}
          />
        </Box>

        {/* Pain Point (Title) */}
        <Typography variant="h5" sx={{
          color: 'white',
          fontWeight: 'bold',
          mb: 1.5,
          lineHeight: 1.3,
          fontFamily: '"SF Pro Rounded", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
          letterSpacing: '-0.02em'
        }}>
          {idea.painpoint || idea.problem_statement}
        </Typography>

        {/* Product Idea Name */}
        <Typography sx={{
          color: '#d1d5db',
          fontSize: '16px',
          mb: 2,
          lineHeight: 1.5
        }}>
          💡 {idea.name}
        </Typography>

        {/* Take a dose CTA */}
        <Button
          variant="contained"
          onClick={handleKillIt}
          sx={{
            mb: 3,
            px: 3,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            fontSize: '15px',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #db2777 100%)',
              boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)'
            }
          }}
        >
          💊 Take a dose
        </Button>

        {/* Scores Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
          mb: 3
        }}>
          {[
            { label: 'Value', value: idea.value_score, color: '#10b981' },
            { label: 'Novelty', value: idea.novelty_score, color: '#6366f1' },
            { label: 'Feasibility', value: idea.feasibility_score, color: '#f59e0b' },
            { label: 'Urgency', value: idea.urgency, color: '#ec4899' }
          ].map(score => (
            <Box key={score.label} sx={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 2,
              p: 1.5
            }}>
              <Typography sx={{ color: '#6b7280', fontSize: '11px', mb: 0.5 }}>
                {score.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>
                  {score.value?.toFixed(1) || '0.0'}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(score.value || 0) * 10}
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: score.color
                    }
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 3 }} />

        {/* Problem Statement */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#9ca3af', fontSize: '12px', mb: 1, fontWeight: 'bold' }}>
            PROBLEM
          </Typography>
          <Typography sx={{ color: '#e5e7eb', fontSize: '14px', lineHeight: 1.6 }}>
            {idea.problem_statement}
          </Typography>
        </Box>

        {/* Target Audience */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <People sx={{ fontSize: 16, color: '#6b7280' }} />
            <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 'bold' }}>
              TARGET AUDIENCE
            </Typography>
          </Box>
          <Typography sx={{ color: '#e5e7eb', fontSize: '14px', lineHeight: 1.6 }}>
            {idea.target_audience}
          </Typography>
        </Box>

        {/* Revenue Model */}
        {idea.revenue_model && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AttachMoney sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 'bold' }}>
                REVENUE MODEL
              </Typography>
            </Box>
            <Typography sx={{ color: '#e5e7eb', fontSize: '14px', lineHeight: 1.6 }}>
              {idea.revenue_model}
            </Typography>
          </Box>
        )}

        {/* MVP Scope */}
        {idea.mvp_scope && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Code sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 'bold' }}>
                MVP SCOPE
              </Typography>
            </Box>
            <Typography sx={{ color: '#e5e7eb', fontSize: '14px', lineHeight: 1.6 }}>
              {idea.mvp_scope}
            </Typography>
          </Box>
        )}

        {/* Key Features */}
        {idea.key_features && idea.key_features.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#9ca3af', fontSize: '12px', mb: 1.5, fontWeight: 'bold' }}>
              KEY FEATURES
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {idea.key_features.map((feature, idx) => (
                <Chip
                  key={idx}
                  label={feature}
                  size="small"
                  sx={{
                    background: 'rgba(255,255,255,0.05)',
                    color: '#d1d5db',
                    fontSize: '12px'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Mobile: Evidence section below (only shows on mobile) */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 3 }} />
          {tweets.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUp sx={{ fontSize: 16, color: '#6b7280' }} />
                <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 'bold' }}>
                  SOURCE
                </Typography>
              </Box>
              <TweetCarousel tweets={tweets} batchSize={4} interval={5000} />
            </Box>
          )}
        </Box>
        </Box>

        {/* Desktop: Evidence sidebar (only shows on desktop) */}
        <Box sx={{
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 80,
          alignSelf: 'start',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto'
        }}>
          {tweets.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUp sx={{ fontSize: 16, color: '#6b7280' }} />
                <Typography sx={{ color: '#9ca3af', fontSize: '12px', fontWeight: 'bold' }}>
                  SOURCE
                </Typography>
              </Box>
              <TweetCarousel tweets={tweets} batchSize={4} interval={5000} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Login Modal */}
      <Dialog
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: '#09090b',
            borderRadius: '12px',
            border: '1px solid #27272a',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          color: '#fafafa',
          pb: 0.5,
          pt: 3,
          fontSize: '18px',
          fontWeight: 600
        }}>
          Sign in to continue
        </DialogTitle>
        <DialogContent sx={{ pb: 4, pt: 1 }}>
          <Typography sx={{
            color: '#71717a',
            textAlign: 'center',
            mb: 3,
            fontSize: '14px'
          }}>
            Connect your X account to start building
          </Typography>

          {/* Login with X only */}
          <Button
            fullWidth
            onClick={handleXLogin}
            disabled={loginLoading}
            sx={{
              py: 1.5,
              background: '#fafafa',
              color: '#09090b',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              '&:hover': {
                background: '#e4e4e7'
              },
              '&.Mui-disabled': {
                background: '#27272a',
                color: '#71717a'
              }
            }}
          >
            {loginLoading ? (
              <CircularProgress size={20} sx={{ color: '#09090b' }} />
            ) : (
              <>
                <XIcon />
                Continue with X
              </>
            )}
          </Button>

          {/* Google login - commented out
          <Button
            fullWidth
            onClick={handleGoogleLogin}
            disabled={loginLoading}
            sx={{
              mt: 1.5,
              py: 1.5,
              background: 'transparent',
              color: '#fafafa',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              border: '1px solid #27272a',
              '&:hover': {
                background: '#27272a'
              }
            }}
          >
            <GoogleIcon />
            <Box sx={{ ml: 1 }}>Continue with Google</Box>
          </Button>
          */}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IdeaDetailPage;
